/**
 * HERO_DEMO_SCRIPT — the scripted "watch Bri work" call transcript that plays
 * inside the hero iPhone (`HeroPhoneDemo`). KP-approved verbatim from the
 * approved mockup — docs/design/mockups/homepage-v5-mockup.html
 * (`PHONE_SCRIPT`). No ElevenLabs session runs here; this is theater.
 *
 * Each line carries a pre-generated ElevenLabs voiceover clip under
 * public/audio/hero-demo/ (Bri = the production Bri voice, caller = a
 * distinct male voice; 64kbps mono, ~200KB total). The demo only touches
 * these files AFTER the user taps the phone — nothing preloads — and when a
 * clip plays, its `ended` event drives advancement to the next line;
 * `talkMs` is the timer fallback when audio can't play (load failure,
 * autoplay policy, headless CI).
 *
 * INVARIANT: `text` may contain ONLY the literal `<b>…</b>` marker (no other
 * tags, no attributes, no nesting) — `HeroPhoneDemo`'s bubble renderer parses
 * exactly that marker via a constrained regex split, never
 * `dangerouslySetInnerHTML`. This file is a source-controlled constant, not
 * user input; if a future edit needs richer markup, extend the renderer
 * deliberately rather than reaching for raw HTML injection.
 */
export interface HeroDemoLine {
  readonly who: 'caller' | 'bri';
  readonly text: string;
  /** Typing-dots duration (ms) before the line's text appears. */
  readonly thinkMs: number;
  /**
   * Fallback hold (ms) for the line's mode (listening/speaking) before the
   * next line starts — used only when the line's audio clip can't play.
   * When audio plays, the clip's `ended` event advances the script instead.
   */
  readonly talkMs: number;
  /** Pre-generated ElevenLabs voiceover for this line (public/ path). */
  readonly audioSrc: string;
}

export const HERO_DEMO_SCRIPT: readonly HeroDemoLine[] = [
  {
    who: 'caller',
    text: 'Hi — my AC just died and it’s 95° out. Can you get someone out today?',
    thinkMs: 900,
    talkMs: 1700,
    audioSrc: '/audio/hero-demo/line-1-caller.mp3',
  },
  {
    who: 'bri',
    text: 'I’m so sorry — let’s fix that fast. I have a technician free between <b>4 and 6pm today</b>. What’s the address?',
    thinkMs: 1100,
    talkMs: 2100,
    audioSrc: '/audio/hero-demo/line-2-bri.mp3',
  },
  {
    who: 'caller',
    text: '142 Alder Court. How much is the visit?',
    thinkMs: 900,
    talkMs: 1400,
    audioSrc: '/audio/hero-demo/line-3-caller.mp3',
  },
  {
    who: 'bri',
    text: 'The diagnostic visit is $89 — <b>waived if you go ahead with the repair</b>. You’re booked for 4–6pm, and I just texted you the confirmation!',
    thinkMs: 1200,
    talkMs: 2400,
    audioSrc: '/audio/hero-demo/line-4-bri.mp3',
  },
] as const;
